import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { GMap } from "primereact/gmap";
import { ProgressSpinner } from "primereact/progressspinner";
import { useTranslation } from "react-i18next";
import { loadGoogleMaps, removeGoogleMaps } from "./googlemaps";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { RoleType } from "../../models/Role";
import { ReceiveStatus } from "../../models/DocumentOfReceive";
import { Warehouse, WarehouseLoading } from "../../models/Warehouse";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getAllDocuments } from "../../services/NewVersionService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { Utility } from "../../core/Utility";
import { activeUser } from "../../services/AuthService";
import "./DashboardPage.scss";

export const DashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();
  const getDateRangeForBusinessYear = (year: number) => {
    const today = new Date();
    const from = new Date(year, 0, 1); // 1.1. godine
    const to =
      year === today.getFullYear()
        ? today // za tekuću godinu – danas
        : new Date(year, 11, 31); // za prošlu – 31.12.

    return { from, to };
  };

  const [googleMapsReady, setGoogleMapsReady] = useState(false);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [warehouseLoading, setWarehouseLoading] = useState<WarehouseLoading[]>(
    []
  );
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState<boolean>(false);

  useEffect(() => {
    loadGoogleMaps(() => {
      setGoogleMapsReady(true);
    });

    return () => {
      removeGoogleMaps();
    };
  }, []);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    // učitavanje magacina + računanje popunjenosti za izabranu poslovnu godinu
    const run = async () => {
      setProcessing(true);
      const wResp = await getAllWarehouses();
      ensureSuccess(wResp);
      const wh = wResp?.data?.result ?? [];
      setWarehousesApi(wh);

      const { from, to } = getDateRangeForBusinessYear(businessYear);
      await formatLoadingWarehouseData(from, to, wh);

      setReady(true);
      setProcessing(false);
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear]);

  const [overlays, setOverlays] = useState<any[]>([]);

  useEffect(() => {
    if (warehousesApi) {
      warehousesApi.map(async (warehouse) => {
        if (warehouse.latitude && warehouse.longitude && google) {
          let newMarker = new google.maps.Marker({
            position: {
              lat: parseFloat(warehouse.latitude),
              lng: parseFloat(warehouse.longitude),
            },
            title: warehouse?.name,
          });
          setOverlays((overlays) => [...overlays, newMarker]);
        }
      });
    }
  }, [googleMapsReady, ready, warehousesApi]);

  const formatLoadingWarehouseData = async (
    dateFrom: Date,
    dateTo: Date,
    warehousesSrc: Warehouse[]
  ) => {
    const dateFromMs = dateFrom.getTime();
    const dateToMs = dateTo.getTime() + 1000 * 60 * 60 * 24; // kraj dana

    // 1) Povuci ulaze/izlaze merkantile za zadati period
    const [inputsResp, outputsResp]: [AxiosResponse, AxiosResponse] =
      await Promise.all([
        getAllDocuments(true, true, dateFrom, dateTo), // merkantila - prijemnice
        getAllDocuments(true, false, dateFrom, dateTo), // merkantila - otpremnice
      ]);

    const mercantileInputs = inputsResp.data.result ?? [];
    const mercantileOutputs = outputsResp.data.result ?? [];

    type Row = {
      id: string;
      warehouse: WarehouseLoading["warehouse"];
      capacityOccupancy: number;
      quantityArrived: number;
      quantityFinished: number;
    };

    const rows = new Map<string, Row>();

    const resolveWarehouse = (doc: any) => {
      // pokušaj po id-u, pa po nazivu, pa po doc.warehouse.id
      if (doc.warehouseId) {
        const wh = warehousesSrc.find((w) => w.id === doc.warehouseId);
        if (wh) return wh;
      }
      if (doc.warehouseName) {
        const wh = warehousesSrc.find((w) => w.name === doc.warehouseName);
        if (wh) return wh;
      }
      if (doc.warehouse && doc.warehouse.id) {
        const wh = warehousesSrc.find((w) => w.id === doc.warehouse.id);
        if (wh) return wh;
      }
      return undefined;
    };

    // 2) Ulazi – popunjavamo capacityOccupancy, quantityArrived, quantityFinished
    for (const doc of mercantileInputs) {
      const ts = new Date(doc.date).getTime();
      if (ts < dateFromMs || ts > dateToMs) continue;

      const warehouse = resolveWarehouse(doc);
      if (!warehouse || !warehouse.id) continue;

      const key = warehouse.id;
      if (!rows.has(key)) {
        rows.set(key, {
          id: warehouse.id,
          warehouse,
          capacityOccupancy: 0,
          quantityArrived: 0,
          quantityFinished: 0,
        });
      }

      const row = rows.get(key)!;
      const brutto = doc.brutto ?? 0;
      const tara = doc.tara ?? 0;
      const netto = doc.netto ?? brutto - tara;

      if (doc.status === ReceiveStatus.Arrived) {
        const diff = brutto - tara;
        row.capacityOccupancy += diff;
        row.quantityArrived += diff;
      } else if (doc.status === ReceiveStatus.Finished) {
        row.capacityOccupancy += netto;
        row.quantityArrived += netto;
        row.quantityFinished += netto;
      }
    }

    // 3) Izlazi – smanjujemo capacityOccupancy
    for (const doc of mercantileOutputs) {
      const ts = new Date(doc.date).getTime();
      if (ts < dateFromMs || ts > dateToMs) continue;

      const warehouse = resolveWarehouse(doc);
      if (!warehouse || !warehouse.id) continue;

      const key = warehouse.id;
      const row = rows.get(key);
      // ako nema ulaza iz tog magacina, ne pravimo novi red samo zbog izlaza
      if (!row) continue;

      const brutto = doc.brutto ?? 0;
      const tara = doc.tara ?? 0;
      const diff = brutto - tara;
      row.capacityOccupancy -= diff;
    }

    setWarehouseLoading(Array.from(rows.values()));
  };

  const warehouseName = (rowData: any) => {
    return rowData.warehouse?.name;
  };

  const capacityOccupancy = (rowData: any) => {
    const isCapacityDefined = rowData.warehouse?.amount !== null ? true : false;
    return isCapacityDefined ? (
      <span className="capacity-occupancy">
        {(
          (100 * rowData.capacityOccupancy) /
          rowData.warehouse?.amount
        ).toFixed(2)}
        %
      </span>
    ) : (
      <span className="capacity-occupancy">
        {t("Nije postavljen kapacitet magacina")}
      </span>
    );
  };

  const capacityWarehouse = (rowData: any) => {
    const isCapacityDefined = rowData.warehouse?.amount !== null ? true : false;
    return isCapacityDefined ? (
      <span className="capacity-occupancy">
        {Utility.addThousandSeparator(rowData.warehouse?.amount)} kg
      </span>
    ) : (
      <span className="capacity-occupancy">
        {t("Nije postavljen kapacitet magacina")}
      </span>
    );
  };

  const capacityFree = (rowData: any) => {
    const isCapacityDefined = rowData.warehouse?.amount !== null ? true : false;
    return isCapacityDefined ? (
      <span className="capacity-free">
        {(
          100 -
          (100 * rowData.capacityOccupancy) / rowData.warehouse?.amount
        ).toFixed(2)}
        %
      </span>
    ) : (
      <span></span>
    );
  };

  const capacityFreeKg = (rowData: any) => {
    const isCapacityDefined = rowData.warehouse?.amount !== null ? true : false;
    return isCapacityDefined ? (
      <span className="capacity-free">
        {Utility.addThousandSeparator(
          rowData.warehouse?.amount - rowData.capacityOccupancy
        )}
      </span>
    ) : (
      <span></span>
    );
  };

  const quantityArrived = (rowData: any) => {
    return (
      <span className="quantity-arrived">
        {Utility.addThousandSeparator(rowData.quantityArrived)}
      </span>
    );
  };

  const quantityFinished = (rowData: any) => {
    return (
      <span className="quantity-finished">
        {Utility.addThousandSeparator(rowData.quantityFinished)}
      </span>
    );
  };

  const infoWindow = useRef(null);

  const onOverlayClick = (event: any) => {
    let isMarker = event.overlay.getTitle !== undefined;

    navigate("/warehouses-overview", {
      state: { warehouseFromDashboard: event?.overlay?.title },
    });
    if (isMarker && google) {
      let title = event.overlay.getTitle(); //@ts-ignore
      infoWindow.current = infoWindow.current || new google.maps.InfoWindow(); //@ts-ignore
      infoWindow.setContent("<div>" + title + "</div>"); //@ts-ignore
      infoWindow.open(event.map, event.overlay);
      event.map.setCenter(event.overlay.getPosition());
    }
  };

  return (
    <PageLayout>
      <div className="table-warehouse-location">
        {activeUser?.role?.name === RoleType.Administrator ||
        activeUser?.role?.name === RoleType.Superadmin ||
        activeUser?.role?.name === RoleType.Logistician ||
        activeUser?.role?.name === RoleType.Director ? (
          <div className="table-warehouse-loading">
            <div className="table-header">
              <div className="header-left">
                <i
                  className="bi bi-truck header-title-icon"
                  style={{
                    fontSize: 15,
                    marginLeft: "1rem",
                    transform: "translate(0, 1px)",
                  }}
                ></i>
                <span className="header-title">{`${t(
                  "Stanje robe na dan"
                )} ${Utility.formatDate(
                  getDateRangeForBusinessYear(businessYear).to
                )}`}</span>
              </div>
              <div className="header-right"></div>
            </div>
            {processing ? (
              <ProgressSpinner />
            ) : (
              <div className="table-body">
                <DataTable value={warehouseLoading} showGridlines stripedRows>
                  <Column body={warehouseName} header={t("Magacin")} />
                  <Column body={capacityWarehouse} header={t("Kapacitet")} />
                  <Column body={capacityOccupancy} header={t("Popunjeno")} />
                  <Column
                    body={quantityArrived}
                    header={t("Pristigla količina")}
                  />
                  <Column
                    body={quantityFinished}
                    header={t("Izvršena količina")}
                  />
                  <Column body={capacityFree} header={t("Slobodno(%)")} />
                  <Column body={capacityFreeKg} header={t("Slobodno(kg)")} />
                </DataTable>
              </div>
            )}
          </div>
        ) : (
          <></>
        )}
        {activeUser?.role?.name === RoleType.Administrator ||
        activeUser?.role?.name === RoleType.Superadmin ||
        activeUser?.role?.name === RoleType.Logistician ||
        activeUser?.role?.name === RoleType.Director ? (
          <div className="google-maps-row">
            {googleMapsReady && (
              <GMap
                options={{
                  center: {
                    lat: 44.787197,
                    lng: 20.457273,
                  },
                  zoom: 7,
                }}
                overlays={overlays}
                onOverlayClick={onOverlayClick}
                className="maps"
              />
            )}
          </div>
        ) : (
          <></>
        )}
      </div>
    </PageLayout>
  );
};
